import { ArtifactCategoryBadge, ArtifactTypeBadge } from '@/components/artifacts';
import type { Artifact } from '@/config/objects/artifacts';
import { Button } from '@startupquickstart/frontend';
import { Clock, Gem, MapPin, Play, RotateCcw, Target, Users } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { ARTIFACT_EFFECT_META, EFFECT_CATEGORY_THEME } from './artifact-effect-meta';
import { EffectVignette, VignetteKeyframes } from './vignettes';

/**
 * Interactive preview of what an artifact does in a hunt. Press "Use" and the
 * phone plays the sequence participants see in the mobile app: the category
 * applied-modal, then the effect itself on the hunt screen with an active-
 * effect banner. Client-only — nothing touches the server.
 */

type DemoPhase = 'idle' | 'applying' | 'active' | 'done';

const APPLYING_MS = 1600;
const ACTIVE_MS = 7000;
const DEFAULT_POINTS = 250;

export function ArtifactEffectDemo({ artifact }: { artifact: Artifact }) {
  const [phase, setPhase] = useState<DemoPhase>('idle');
  const timers = useRef<number[]>([]);

  const meta = ARTIFACT_EFFECT_META[artifact.artifactType];
  const theme = EFFECT_CATEGORY_THEME[artifact.artifactCategory];
  const ThemeIcon = theme.icon;
  const TypeIcon = meta.icon;
  const points = artifact.effectValue ?? DEFAULT_POINTS;
  const timed = artifact.effectDuration != null && artifact.effectDuration > 0;

  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach((t) => window.clearTimeout(t));
  }, []);

  const run = () => {
    timers.current.forEach((t) => window.clearTimeout(t));
    setPhase('applying');
    timers.current = [
      window.setTimeout(() => setPhase('active'), APPLYING_MS),
      window.setTimeout(() => setPhase('done'), APPLYING_MS + ACTIVE_MS),
    ];
  };

  const modalTitle = meta.pov === 'rival' ? theme.rivalTitle : theme.appliedTitle;

  return (
    <div className="space-y-4">
      <VignetteKeyframes />
      <div className="flex items-center gap-2 rounded-lg border border-dashed bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
        <Play className="h-3.5 w-3.5 shrink-0" />
        Preview — the sequence participants see in the app when this artifact is used. Nothing is
        sent to the server.
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,20rem)_1fr]">
        {/* ── Phone simulation ─────────────────────────────────────── */}
        <div className="flex flex-col items-center gap-3">
          <p className="text-xs font-medium text-muted-foreground">
            {meta.pov === 'rival' ? 'Watching: a rival team’s phone' : 'Watching: your team’s phone'}
          </p>

          <div className="relative w-[280px] shrink-0 rounded-[2.2rem] border-[6px] border-slate-950 bg-slate-900 shadow-xl">
            {/* Notch */}
            <div className="absolute left-1/2 top-2 z-30 h-4 w-24 -translate-x-1/2 rounded-full bg-slate-950" />

            <div className="relative h-[480px] overflow-hidden rounded-[1.85rem]">
              {/* Status bar */}
              <div className="flex items-center justify-between px-5 pb-1 pt-8 text-[10px] font-medium text-slate-400">
                <span>10:24</span>
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3" /> Beacon Hill Bunch
                </span>
              </div>

              {/* Active-effect banner (mirrors mobile EffectBanner) */}
              <div
                className={`mx-3 flex items-center gap-2 rounded-lg px-2.5 py-1.5 transition-all duration-300 ${
                  phase === 'active'
                    ? `${theme.soft} opacity-100`
                    : 'pointer-events-none opacity-0'
                }`}
                aria-hidden={phase !== 'active'}
              >
                <TypeIcon className="h-3.5 w-3.5 shrink-0" />
                <span className="min-w-0 flex-1 truncate text-[11px] font-semibold">
                  {artifact.name}
                </span>
                <span className="text-[10px] tabular-nums opacity-80">
                  {timed ? `${artifact.effectDuration} min` : 'Until used'}
                </span>
              </div>
              {/* Draining progress bar for the demo window */}
              {phase === 'active' && (
                <div className="mx-3 mt-1 h-0.5 overflow-hidden rounded bg-slate-700">
                  <div
                    className={`artifact-demo-animated h-full ${theme.accent}`}
                    style={{ animation: `artifact-demo-drain ${ACTIVE_MS}ms linear forwards` }}
                  />
                </div>
              )}

              {/* Mock hunt screen */}
              <div className="space-y-3 px-3 pt-3">
                {/* Map card */}
                <div className="relative h-40 overflow-hidden rounded-xl bg-slate-800">
                  <svg className="absolute inset-0 h-full w-full text-slate-700" aria-hidden>
                    <path
                      d="M20 120 Q 80 90 130 100 T 250 60"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeDasharray="4 6"
                    />
                  </svg>
                  <MapPin className="absolute left-[30%] top-[38%] h-5 w-5 text-emerald-400" />
                  <MapPin className="absolute left-[68%] top-[22%] h-5 w-5 text-slate-500" />
                  <span className="absolute bottom-2 left-2 rounded bg-slate-900/80 px-1.5 py-0.5 text-[9px] font-medium text-slate-300">
                    Hunt Map
                  </span>
                </div>

                {/* Challenge card */}
                <div className="rounded-xl bg-slate-800 p-3">
                  <p className="text-xs font-semibold text-slate-100">Make Way for Ducklings</p>
                  <p className="mt-0.5 text-[10px] text-slate-400">
                    Photo challenge · {points} pts
                  </p>
                </div>

                {/* Points chip */}
                <div className="flex justify-end">
                  <span className="rounded-full bg-slate-800 px-2.5 py-1 text-[11px] font-bold tabular-nums text-slate-100">
                    1,240 pts
                  </span>
                </div>
              </div>

              {/* Effect vignette */}
              {phase === 'active' && (
                <EffectVignette type={artifact.artifactType} points={points} />
              )}

              {/* Applied modal (mirrors mobile EffectAppliedModal) */}
              {phase === 'applying' && (
                <div className="absolute inset-0 z-40 flex items-center justify-center bg-slate-950/70 p-6">
                  <div className="w-full rounded-2xl bg-slate-900 p-5 text-center shadow-2xl motion-safe:animate-in motion-safe:zoom-in-95">
                    <span
                      className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full ${theme.accent} text-white`}
                    >
                      <ThemeIcon className="h-6 w-6" />
                    </span>
                    <p className="mt-3 text-sm font-bold text-white">{modalTitle}</p>
                    <p className="mt-1 text-xs text-slate-400">{artifact.name}</p>
                  </div>
                </div>
              )}

              {/* Idle / done scrim with the artifact itself */}
              {(phase === 'idle' || phase === 'done') && (
                <div className="absolute inset-0 z-40 flex flex-col items-center justify-center gap-3 bg-slate-950/80 p-6 text-center">
                  {artifact.imageUrl ? (
                    <img
                      src={artifact.imageUrl}
                      alt={artifact.name}
                      className="h-20 w-20 rounded-xl object-cover shadow-lg"
                    />
                  ) : (
                    <span className="flex h-20 w-20 items-center justify-center rounded-xl bg-slate-800">
                      <Gem className="h-9 w-9 text-slate-500" />
                    </span>
                  )}
                  <p className="text-sm font-semibold text-white">{artifact.name}</p>
                  {phase === 'done' && (
                    <p className="text-[11px] text-slate-400">Effect expired</p>
                  )}
                </div>
              )}
            </div>
          </div>

          <Button onClick={run} disabled={phase === 'applying' || phase === 'active'}>
            {phase === 'done' ? (
              <>
                <RotateCcw className="h-4 w-4" /> Replay
              </>
            ) : (
              <>
                <Play className="h-4 w-4" /> Use {artifact.name}
              </>
            )}
          </Button>
        </div>

        {/* ── What it does ─────────────────────────────────────────── */}
        <div className="space-y-4">
          <div className="rounded-xl border bg-card p-5 shadow-sm">
            <div className="flex flex-wrap items-center gap-2">
              <ArtifactTypeBadge type={artifact.artifactType} />
              <ArtifactCategoryBadge category={artifact.artifactCategory} />
            </div>
            <p className="mt-3 text-sm text-foreground">{meta.summary}</p>
          </div>

          <div className="rounded-xl border bg-card p-5 shadow-sm">
            <h3 className="text-sm font-semibold">How it plays out</h3>
            <ol className="mt-3 space-y-2.5">
              {meta.steps.map((step, i) => (
                <li key={step} className="flex gap-3 text-sm text-muted-foreground">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-semibold text-foreground">
                    {i + 1}
                  </span>
                  {step}
                </li>
              ))}
            </ol>
          </div>

          <div className="rounded-xl border bg-card p-5 shadow-sm">
            <h3 className="text-sm font-semibold">This artifact’s settings</h3>
            <dl className="mt-3 grid gap-3 sm:grid-cols-2">
              <div className="flex items-start gap-2.5">
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div>
                  <dt className="text-xs text-muted-foreground">Effect duration</dt>
                  <dd className="text-sm font-medium">
                    {timed ? `${artifact.effectDuration} minutes` : 'Instant / until used'}
                  </dd>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <Target className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div>
                  <dt className="text-xs text-muted-foreground">Targets</dt>
                  <dd className="text-sm font-medium">
                    {artifact.targetCount === 0
                      ? 'All other teams'
                      : `${artifact.targetCount} team${artifact.targetCount === 1 ? '' : 's'}`}
                  </dd>
                </div>
              </div>
            </dl>
            {artifact.artifactType === 'shield' && (
              <p className="mt-3 text-xs text-muted-foreground">
                Shields are consumed automatically: the server blocks the first offensive artifact
                aimed at the holding team and removes the shield.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
